import { focusRing } from '../../Themes/base/focusRing';
import { styled } from '../../Themes';
import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { borderRadius } from '../../Utilities/utils/Theming';
import { IDummyProps, TSize } from './types';

const LinkFontSizes: { [key: string]: string } = {
	default: 'inherit',
	compact: 'var(--epods--link-compact-font-size)',
	mini: 'var(--epods--link-mini-font-size)',
	large: 'var(--epods--link-large-ont-size)'
};

const LinkLineHeightSizes: { [key: string]: string } = {
	default: 'inherit',
	compact: 'var(--epods--link-compact-line-height)',
	mini: 'var(--epods--link-mini-line-height)',
	large: 'var(--epods--link-large-line-height)'
};

const getFontSize = (size: TSize) => {
	const isPresetFontSize = ['default', 'compact', 'mini', 'large'].includes(size);
	return isPresetFontSize ? LinkFontSizes[size] : size;
};

const getLineHeight = (size: TSize) => {
	const isPresetLineHeight = ['default', 'compact', 'mini', 'large'].includes(size);
	return isPresetLineHeight ? LinkLineHeightSizes[size] : size;
};

/**
 * LINK STYLES
 */
export const linkStyles = ({ disabled, theme, size }: { disabled: boolean; theme: typeof Theme; size: TSize }) => {
	return {
		textDecoration: 'none',
		fontFamily: theme.link.linkTextFamily,
		fontWeight: 'normal',
		fontSize: getFontSize(size),
		lineHeight: getLineHeight(size),
		cursor: disabled ? 'not-allowed' : 'pointer',
		...borderRadius('0px'),
		borderTopWidth: '0px',
		borderRightWidth: '0px',
		borderBottomWidth: '1px',
		borderLeftWidth: '0px',
		paddingRight: '0',
		paddingLeft: '0',
		borderBottomStyle: 'solid',
		borderBottomColor: 'transparent',
		backgroundColor: 'transparent',
		color: disabled ? theme.link.linkDisabled : theme.colors.linkText,
		display: 'inline-flex',
		alignItems: 'center',
		':hover': {
			borderBottomWidth: '1px',
			marginBottom: '-1px',
			color: disabled ? theme.link.linkDisabled : theme.colors.linkHover,
			borderBottomColor: 'unset',
			textDecoration: 'none',
		},
		':active': {
			color: disabled ? theme.link.linkDisabled : theme.colors.linkActive,
			textDecoration: 'none',
		},
		':visited': {
			color: disabled ? theme.link.linkDisabled : theme.colors.linkVisited,
			textDecoration: 'none',
		},
		':focus': focusRing,
	};
};

export const StyledLink = styled<IDummyProps, 'a', typeof Theme>('a', ({ disabled, $theme, size = 'default' }) => ({
	textDecoration: 'none',
	fontFamily: $theme.link.linkTextFamily,
	fontWeight: 'normal',
	fontSize: getFontSize(size),
	lineHeight: getLineHeight(size),
	cursor: disabled ? 'not-allowed' : 'pointer',
	borderBottomWidth: '1px',
	borderBottomStyle: 'solid',
	borderBottomColor: 'transparent',
	color: disabled ? $theme.link.linkDisabled : $theme.colors.linkText,
	':hover': {
		borderBottomWidth: '1px',
		color: disabled ? $theme.link.linkDisabled : $theme.colors.linkHover,
		borderBottomColor: 'unset',
		textDecoration: 'none',
	},
	':active': {
		color: disabled ? $theme.link.linkDisabled : $theme.colors.linkActive,
		textDecoration: 'none',
	},
	':visited': {
		color: disabled ? $theme.link.linkDisabled : $theme.colors.linkVisited,
		textDecoration: 'none',
	},
	':focus': focusRing,
}));

/** Icons */
export const iconStyles = ({ disabled, size = 'default' }: IDummyProps, theme: typeof Theme) => ({
	fontSize: getFontSize(size),
	lineHeight: getLineHeight(size),
	verticalAlign: 'sub',
	color: disabled ? theme.link.linkDisabled : theme.colors.linkText,
	':hover': {
		color: disabled ? theme.link.linkDisabled : theme.colors.linkHover,
	},
	':active': {
		color: disabled ? theme.link.linkDisabled : theme.colors.linkActive,
	},
	':visited': {
		color: disabled ? theme.link.linkDisabled : theme.colors.linkVisited,
	},
});

export const wrapperStyle = () => {
	return { display: 'inline-flex' };
};

export const iconBeforeStyles = (props: IDummyProps, theme: typeof Theme) => {
	return { marginRight: props.children ? theme.link.iconMargin : '0' };
};

export const iconAfterStyles = (props: IDummyProps, theme: typeof Theme) => {
	return { marginLeft: props.children ? theme.link.iconMargin : '0' };
};
